import { differenceInCalendarDays } from 'date-fns';
import * as React from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { translate, translateFirstUpper } from 'secullum-i18n';
import {
  Card,
  ErrorMessage,
  isTablet,
  Message,
  RangeDatePicker,
  Space,
  TextBox
} from 'secullum-react-native-ui';
import {
  estiloBotaoDestaque,
  estiloTextoBotaoDestaque
} from '../../components/BotaoDestaque';
import { ButtonBar } from '../../components/ButtonBar';
import Api from '../../modules/api';
import { Cinza50, getTheme } from '../../modules/layout';
import { DadosFuncionario, TipoSolicitacao } from '../../modules/types';

interface Props {
  dados: DadosFuncionario;
  onConcluir: () => void;
  onCancelar: () => void;
  onErroConexao?: (mensagem: string, onTentarNovamente: () => void) => void;
}

interface State {
  dataInicio: Date;
  dataFim: Date;
  observacoes: string;
  erros: { [key: string]: string };
  resultado: 'sucesso' | 'erro' | null;
  erroGeral: string;
  bloquearEnviar: boolean;
}

class CardSolicitarFerias extends React.Component<Props, State> {
  state: State = {
    dataInicio: new Date(),
    dataFim: new Date(),
    observacoes: '',
    erros: {},
    resultado: null,
    erroGeral: '',
    bloquearEnviar: false
  };

  calcularDias = () => {
    const { dataInicio, dataFim } = this.state;

    if (dataFim < dataInicio) {
      return '';
    }

    // +1 para considerar o último dia.
    return String(differenceInCalendarDays(dataFim, dataInicio) + 1);
  };

  handleEnviar = () => {
    const api = new Api();
    const { dataInicio, dataFim, observacoes, bloquearEnviar } = this.state;

    if (bloquearEnviar) {
      return;
    }

    this.setState({ bloquearEnviar: true });

    api.postSolicitacao(
      {
        data: dataInicio,
        justificativaId: null,
        entrada1: '',
        saida1: '',
        entrada2: '',
        saida2: '',
        entrada3: '',
        saida3: '',
        entrada4: '',
        saida4: '',
        entrada5: '',
        saida5: '',
        tipo: TipoSolicitacao.SolicitacaoFerias,
        observacoes,
        foto: null,
        temFoto: false,
        registroPendente: false,
        existePeriodoEncerrado: false,
        dataInicioAfastamento: dataInicio,
        dataFimAfastamento: dataFim,
        funcionarioId: this.props.dados.id
      },
      {
        onSuccess: () => {
          this.setState({ resultado: 'sucesso', bloquearEnviar: false });
        },
        onError: err => {
          this.setState({ bloquearEnviar: false });

          if (err.name === 'TimeoutError' && this.props.onErroConexao) {
            this.props.onErroConexao(err.message, this.handleEnviar);
          } else {
            this.setState({ resultado: 'erro', erroGeral: err.message });
          }
        },
        onValidationError: (_, errorObj) => {
          this.setState({ erros: errorObj, bloquearEnviar: false });
        }
      }
    );
  };

  render() {
    const {
      dataInicio,
      dataFim,
      observacoes,
      erros,
      resultado,
      erroGeral,
      bloquearEnviar
    } = this.state;
    const plataformaWeb = Platform.OS === 'web';

    return (
      <>
        <Card>
          <Card.Header
            nativeID="ferias-solicitar-texto-explicativo"
            title={translate(
              'A solicitação de férias deve ser utilizada para solicitar ao seu gestor o período de férias desejado.'
            )}
            titleStyle={[styles.titleHeader, { color: Cinza50 }]}
          />
          <Card.Section>
            <View style={plataformaWeb && styles.centralizeView}>
              <RangeDatePicker
                nativeID="ferias-solicitar-periodo"
                label={translate('Insira o Período')}
                startDate={dataInicio}
                endDate={dataFim}
                onStartDateChange={date =>
                  date && this.setState({ dataInicio: date })
                }
                onEndDateChange={date =>
                  date && this.setState({ dataFim: date })
                }
              />
              <ErrorMessage
                nativeID="ferias-solicitar-erro-data"
                message={
                  erros.dataInicioAfastamento || erros.dataFimAfastamento
                }
              />
              <Space />
              <TextBox
                nativeID="ferias-solicitar-dias"
                label={translateFirstUpper('dias')}
                value={this.calcularDias()}
                editable={false}
              />
              <Space />
              <TextBox
                nativeID="ferias-solicitar-observacao"
                label={translate('Observação')}
                value={observacoes}
                multiline={true}
                maxLength={100}
                inputStyle={
                  plataformaWeb ? { minHeight: 60 } : { maxHeight: 60 }
                }
                onChange={observacoes => this.setState({ observacoes })}
              />
              <ErrorMessage
                nativeID="ferias-solicitar-erro-observacao"
                message={erros.observacoes}
              />
              <Space />
              <ButtonBar
                buttonBarStyle={plataformaWeb && styles.centralizeView}
                leftButton={{
                  nativeID: 'ferias-solicitar-cancelar',
                  text: translate('Cancelar'),
                  primary: false,
                  onPress: this.props.onCancelar
                }}
                rightButton={{
                  nativeID: 'ferias-solicitar-enviar',
                  text: translate('Enviar'),
                  disabled: bloquearEnviar,
                  onPress: this.handleEnviar,
                  style: estiloBotaoDestaque,
                  textStyle: estiloTextoBotaoDestaque
                }}
              />
            </View>
          </Card.Section>
        </Card>
        <Message
          nativeID="ferias-solicitar-sucesso"
          message={translate('Solicitação enviada com sucesso')}
          visible={resultado === 'sucesso'}
          onRequestClose={this.props.onConcluir}
        />
        <Message
          nativeID="ferias-solicitar-erro"
          type="warning"
          message={erroGeral}
          visible={resultado === 'erro'}
          onRequestClose={() =>
            this.setState({ resultado: null, erroGeral: '' })
          }
        />
      </>
    );
  }
}

const theme = getTheme();

const styles = StyleSheet.create({
  centralizeView: {
    margin: 'auto',
    maxWidth: 350,
    width: '100%'
  },
  titleHeader: {
    color: theme.textColor2,
    fontSize: isTablet() ? 16 : 12,
    fontFamily: theme.fontFamily2
  }
});

export default CardSolicitarFerias;
